/**
 * 店舗オーナー向け trusted 端末が見る「承認待ち新デバイス」一覧 hook (#867 v3+).
 *
 * 顧客側 `useDevicePendingApprovals` (#819 B-main Phase 2b) の店舗版。
 * `/store-devices/pending` (trusted device からのみ・未信頼 caller は 403)
 * を呼び、`StoreDeviceApprovalPromptModal` を出すための一覧を提供する。
 *
 * - enabled (= 自端末が trusted) が true のときだけ fetch / poll
 * - fail-open: 取得失敗は「pending 無し」に倒す
 * - poll 4s (顧客側と同じく対称) — trusted 端末が後から現れた pending を
 *   リロード無しで検知する
 * - approve/deny 後は refresh() で即時再取得
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'

export interface StorePendingDevice {
  deviceId: string
  deviceLabel?: string | null
  firstSeen?: unknown
  lastIpAddress?: string
  lastUserAgent?: string
  deniedAt?: unknown
}

const POLL_MS = 4000

function samePending(a: StorePendingDevice[], b: StorePendingDevice[]): boolean {
  if (a.length !== b.length) return false
  const key = (p: StorePendingDevice) => `${p.deviceId}:${p.deniedAt ? 1 : 0}`
  const sa = a.map(key).sort()
  const sb = b.map(key).sort()
  return sa.every((v, i) => v === sb[i])
}

export function useStoreDevicePendingApprovals(enabled: boolean): {
  pending: StorePendingDevice[]
  refresh: () => void
} {
  const [fetched, setFetched] = useState<StorePendingDevice[]>([])
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let did = ''
    try {
      did = getDeviceId()
    } catch {
      did = ''
    }
    if (!did) return

    const fetchPending = () => {
      api
        .listStorePendingDevices(did)
        .then((r) => {
          if (cancelled) return
          const next = (r?.pending || []).filter((p) => !p.deniedAt)
          setFetched((prev) => (samePending(prev, next) ? prev : next))
        })
        .catch(() => {
          if (!cancelled) setFetched((prev) => (prev.length === 0 ? prev : []))
        })
    }

    fetchPending()
    const timer = setInterval(fetchPending, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [enabled, tick])

  return { pending: enabled ? fetched : [], refresh }
}
